import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { listReports, resolveReport } from '../lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Label,
  Loading,
  PageHeader,
  TextInput,
  formatDate,
} from '../components/ui';

export function ReportsPage(): JSX.Element {
  const [status, setStatus] = useState<'open' | 'resolved'>('open');
  const q = useQuery({
    queryKey: ['reports', status],
    queryFn: () => listReports(status),
  });

  return (
    <>
      <PageHeader title="Reports" subtitle="User-submitted reports against other users or rides." />
      <div className="mb-4 flex gap-2">
        <Button variant={status === 'open' ? 'primary' : 'secondary'} onClick={() => setStatus('open')}>
          Open
        </Button>
        <Button variant={status === 'resolved' ? 'primary' : 'secondary'} onClick={() => setStatus('resolved')}>
          Resolved
        </Button>
      </div>
      {q.isLoading && <Loading />}
      {q.isError && <ErrorBox message={(q.error as Error).message} />}
      {q.data && q.data.items.length === 0 && <EmptyState>No reports here.</EmptyState>}
      <div className="space-y-3">
        {q.data?.items.map((r) => <ReportCard key={r.id} report={r} />)}
      </div>
    </>
  );
}

function ReportCard({
  report,
}: {
  report: {
    id: string;
    reporterId: string;
    reporterDisplayName: string | null;
    reportedUserId: string | null;
    reportedDisplayName: string | null;
    rideId: string | null;
    bookingId: string | null;
    reason: string;
    description: string | null;
    status: string;
    resolutionNote: string | null;
    createdAt: number;
  };
}): JSX.Element {
  const qc = useQueryClient();
  const [action, setAction] = useState<'dismiss' | 'warn' | 'suspend'>('dismiss');
  const [note, setNote] = useState('');
  const [days, setDays] = useState(7);

  const mut = useMutation({
    mutationFn: () =>
      resolveReport(report.id, {
        action,
        note,
        suspendUntilMs:
          action === 'suspend' ? (days > 0 ? Date.now() + days * 24 * 3600 * 1000 : null) : undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });

  const isOpen = report.status === 'open';

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-brand-navy">
            <Badge tone="warning">{report.reason}</Badge>
            {isOpen ? null : <Badge tone="success">{report.status}</Badge>}
          </p>
          <p className="mt-1 text-xs text-text-soft">
            By {report.reporterDisplayName ?? report.reporterId.slice(0, 12)} ·{' '}
            {formatDate(report.createdAt)}
          </p>
          {report.reportedUserId && (
            <p className="mt-1 text-sm">
              Against:{' '}
              <Link
                to={`/users/${report.reportedUserId}`}
                className="font-semibold text-brand-navy hover:underline"
              >
                {report.reportedDisplayName ?? report.reportedUserId.slice(0, 12)}
              </Link>
            </p>
          )}
          {report.description && (
            <p className="mt-2 rounded bg-bg p-3 text-sm text-text-soft">{report.description}</p>
          )}
        </div>
      </div>
      {isOpen && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[160px_120px_1fr_auto]">
          <div>
            <Label>Action</Label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as 'dismiss' | 'warn' | 'suspend')}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            >
              <option value="dismiss">Dismiss</option>
              <option value="warn">Warn user (bot message)</option>
              <option value="suspend">Suspend user</option>
            </select>
          </div>
          {action === 'suspend' ? (
            <div>
              <Label>Days (0 = forever)</Label>
              <TextInput
                type="number"
                min={0}
                value={String(days)}
                onChange={(e) => setDays(Number(e.target.value))}
              />
            </div>
          ) : (
            <div />
          )}
          <div>
            <Label>Note</Label>
            <TextInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Required for audit log"
            />
          </div>
          <div className="self-end">
            <Button onClick={() => mut.mutate()} disabled={!note.trim() || mut.isPending}>
              {mut.isPending ? 'Working…' : 'Apply'}
            </Button>
          </div>
        </div>
      )}
      {!isOpen && report.resolutionNote && (
        <p className="mt-2 text-xs text-text-soft">
          <strong>Resolution:</strong> {report.resolutionNote}
        </p>
      )}
      {mut.isError && (
        <div className="mt-3">
          <ErrorBox message={(mut.error as Error).message} />
        </div>
      )}
    </Card>
  );
}
